import * as React from 'react';
import { IReceiptConfirmationProps } from './ReceiptConfirmationDialog.properties';
import FormViewModal from 'components/common/Modals/FormViewModal';
import { IActionItem } from 'components/common/Modals/FormViewModal.properties';
import ReceiptConfirmationForm from './ReceiptConfirmationDialog.form';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField';
import SelectContainer from 'components/SelectContainer';
import { useUpdateOptions } from 'api/documentOutput';
import { showSnackBar } from 'components/common/SnackBars/SnackBar.hooks';

const ReceiptConfirmationDialog = (props: IReceiptConfirmationProps): JSX.Element => {
  const {
    open, values, unpackingReportDocumentOutputOptions, shelfLabelDocumentOutputOptions, stockLabelDocumentOutputOptions,
    setUnpackingReportDocumentOutputOptions, setStockLabelDocumentOutputOptions, setShelfLabelDocumentOutputOptions, onOk: onApply,
    onCancel, initialValues, changeFormFieldValue, currentUserId, formSyncErrors, isProcessing, supplierId
  } = props;

  const [printerValue, setPrinterValue] = React.useState<string>('');
  const [sortValue, setSortValue] = React.useState<string>('');
  const [selectedSortValue, setSelectedSortValue] = React.useState<string>('');
  const applyOutputOptionsMutation = useUpdateOptions();

  const confirmationActions = (): IActionItem[] => {
    return [
      {
        title: 'Ok',
        disabled: !!isProcessing,
        listener: () => {
          if (isValidFormData()) {
            onApply({
              ...values,
              SortOption: selectedSortValue,
              UnpackingOutput: { ...unpackingReportDocumentOutputOptions },
              ShelfOutput: { ...shelfLabelDocumentOutputOptions },
              StockOutput: { ...stockLabelDocumentOutputOptions },
            });
          }
        }
      },
      {
        title: 'Cancel',
        isDefault: true,
        listener: () => {
          onCancel();
        }
      }
    ];
  };

  React.useEffect(
    () => {
      if (open && initialValues) {
        setUnpackingReportDocumentOutputOptions(initialValues?.inlineObject?.UnpackingOutput);
        setShelfLabelDocumentOutputOptions(initialValues?.inlineObject?.ShelfOutput);
        setStockLabelDocumentOutputOptions(initialValues?.inlineObject?.StockOutput);
      }
    },
    [open, initialValues]);

  const isValidFormData = (): boolean => {
    if (formSyncErrors && Object.keys(formSyncErrors).length > 0) {
      const requiredError = Object.entries(formSyncErrors).find((item) => item.includes('Required'));
      if (requiredError) {
        showSnackBar({ variant: 'warning', message: 'Please fill in required fields.' });
      } else {
        const firstError = Object.keys(formSyncErrors)[0];
        showSnackBar({ variant: 'warning', message: `Please enter valid ${firstError}` });
      }

      return false;
    }

    return true;
  };

  const processUnpackingReport = React.useCallback(
    async (_) => {
      const response = await applyOutputOptionsMutation.mutateAsync({
        TTDocout: { ...unpackingReportDocumentOutputOptions, PrinterName: printerValue },
        urlQueryParams: {
          SendDocument: false
        }
      });

      if (response.Status) {
        setUnpackingReportDocumentOutputOptions(response?.DocumentOutputOptions?.inlineObject);
        setSelectedSortValue(sortValue);
      }

    },
    [printerValue, sortValue]);

  function unPackingReportModalContent(): JSX.Element {

    return <div style={{
      height: '150px',
      minWidth: '500px',
      padding: '2rem'
    }}>
      <BasicLookupActionField
        placeholder={'Printer'}
        column1Field={'Display'}
        column2Field={'Label'}
        label={'Printer'}
        size={'large'}
        lookupName={'Printer'}
        value={unpackingReportDocumentOutputOptions?.PrinterName}
        display={'Print to Printer'}
        onSelectedItemChange={(item, _) => { setPrinterValue(item?.Code ?? ''); }}
        suppressDescription={true}
        style={{ width: '80%' }}
      />
      <SelectContainer
        size={'medium'}
        value={sortValue}
        label='Sort by'
        name='Sort'
        initialValues={selectedSortValue}
        apiPrefs={({
          LookupType: 'Fixed',
          path: '/CustomTypes/Lookup/ProductReceiptUnpackingOption/Search',
          method: 'POST'
        })}
        params={{
          SearchText: '',
          BatchPage: 1,
          BatchSize: 100
        }}
        onChange={(v) => {
          setSortValue(v);
        }}
        required={false}
        defaultFirstValue={true}
      />.
    </div>;
  }

  return (<FormViewModal
    open={open}
    loading={false}
    title={'Receipt Confirmation'}
    formProps={{
      style: {
        height: '585px',
        minWidth: '535px'
      },
      formSchema: ReceiptConfirmationForm,
      initialValues: {
        ...initialValues?.inlineObject,
        UnpackingOutput: initialValues?.inlineObject?.UnpackingOutput?.Summary ?? '',
        ShelfOutput: initialValues?.inlineObject?.ShelfOutput?.Summary ?? '',
        StockOutput: initialValues?.inlineObject?.StockOutput?.Summary ?? '',
        UserId: currentUserId,
      },
      valuesSchema: initialValues ? initialValues?.schema : {},
      supplierId: supplierId,
      fieldExtensions: {
        UnpackingOutput: {
          props: {
            overrideDefaultModal: true,
            additionalDialogProps: {
              title: 'Unpacking Report',
              content: unPackingReportModalContent(),
              onApply: processUnpackingReport,
              onCancel: () => {
                setSortValue(selectedSortValue);
              }
            }
          }
        },
        OutputStock: {
          onChange: (e) => {
            if (e.target.checked) {
              changeFormFieldValue('OutputShelf', !e.target.checked);
            }
          }
        },
        OutputShelf: {
          onChange: (e) => {
            if (e.target.checked) {
              changeFormFieldValue('OutputStock', !e.target.checked);
            }
          }
        }
      }
    }}
    actions={confirmationActions()}
    dialogActionsButtons={true}
    dialogActionsShadow={false}
  />);
};

export default ReceiptConfirmationDialog;
